// 组织层级工具：集团 → 二级板块 → 三级子公司 → 四级分公司
// 扁平 + parent/level 结构，配合预算的子孙集合实现按子树汇总/下钻与数据作用域过滤
import { ORG_NODES } from '../mock/constants';

const NODE_BY_ID = Object.fromEntries(ORG_NODES.map((n) => [n.value, n]));

// 记忆化的子孙集合（含自身）
const DESC_CACHE = {};
export function getDescendantIds(nodeId) {
  if (nodeId == null || !NODE_BY_ID[nodeId]) return new Set();
  if (DESC_CACHE[nodeId]) return DESC_CACHE[nodeId];
  const out = new Set([nodeId]);
  ORG_NODES.filter((n) => n.parent === nodeId).forEach((child) => {
    getDescendantIds(child.value).forEach((id) => out.add(id));
  });
  DESC_CACHE[nodeId] = out;
  return out;
}

// 叶子子孙（三/四级单位），含自身（若自身即叶子）
export function getDescendantLeafIds(nodeId) {
  return [...getDescendantIds(nodeId)].filter(
    (id) => !ORG_NODES.some((n) => n.parent === id)
  );
}

// 祖先链（根→当前序，含自身）
export function getAncestorIds(nodeId) {
  const out = [];
  let cur = nodeId;
  while (cur && NODE_BY_ID[cur]) {
    out.unshift(cur);
    cur = NODE_BY_ID[cur].parent;
  }
  return out;
}

export function getParentId(nodeId) {
  return NODE_BY_ID[nodeId]?.parent ?? null;
}

export function getLevel(nodeId) {
  return NODE_BY_ID[nodeId]?.level ?? null;
}

export function getNode(nodeId) {
  return NODE_BY_ID[nodeId] || null;
}

export function getLabel(nodeId) {
  return NODE_BY_ID[nodeId]?.label ?? nodeId ?? '--';
}

// 组织路径标签数组，如 [集团, 二级板块, 三级公司, 四级分公司]
export function getOrgPath(nodeId) {
  return getAncestorIds(nodeId).map((id) => NODE_BY_ID[id].label);
}

export function getOrgPathString(nodeId, sep = ' / ') {
  if (nodeId == null || !NODE_BY_ID[nodeId]) return '--';
  return getOrgPath(nodeId).join(sep);
}

// 简短路径：去掉集团前缀，便于表格/头部展示，如 "再生资源板块 / 资环链金再生资源公司"
export function getOrgPathShort(nodeId, sep = ' / ') {
  const path = getOrgPath(nodeId);
  return path.length > 1 ? path.slice(1).join(sep) : path.join(sep);
}

// 板块内路径：从三级起至当前节点的标签（去掉集团与二级板块前缀）
// 三级 -> "有色金属投资公司"；四级 -> "资环链金再生资源公司 / 华东再生分公司"
export function getSegmentPath(nodeId, sep = ' / ') {
  if (nodeId == null || !NODE_BY_ID[nodeId]) return '--';
  const path = getAncestorIds(nodeId)
    .filter((id) => NODE_BY_ID[id].level > 2)
    .map((id) => NODE_BY_ID[id].label);
  return path.length ? path.join(sep) : NODE_BY_ID[nodeId].label;
}

export function isDescendantOf(maybeChildId, ancestorId) {
  return getDescendantIds(ancestorId).has(maybeChildId);
}

// 任意节点所属的二级板块（审批路由核心）：集团返回 null
export function getLevel2Ancestor(nodeId) {
  return getAncestorIds(nodeId).find((id) => NODE_BY_ID[id].level === 2) || null;
}

// 构建 <TreeSelect treeData> / <Cascader options> / <Tree treeData> 的嵌套结构
// leafOnly: 仅叶子可选；minLevel: 限制最大层级；includeRoot: 是否含集团根节点
export function buildOrgTreeData({ leafOnly = false, includeRoot = true, minLevel } = {}) {
  const build = (parentId) =>
    ORG_NODES.filter((n) => n.parent === parentId)
      .filter((n) => !minLevel || n.level <= minLevel)
      .map((n) => {
        const children = build(n.value);
        const isLeaf = children.length === 0;
        if (leafOnly && !isLeaf) return null;
        // 同时提供 key/value/title/label，兼容 <Tree>(key/title) 与 <TreeSelect>/<Cascader>(value/label)
        return { key: n.value, value: n.value, title: n.label, label: n.label, ...(children.length ? { children } : {}) };
      })
      .filter(Boolean);
  if (includeRoot) {
    const root = NODE_BY_ID['group'];
    const children = build('group');
    return [{ key: root.value, value: root.value, title: root.label, label: root.label, ...(children.length ? { children } : {}) }];
  }
  return build('group');
}

// 按用户组织子树过滤记录（标量字段）。集团看全部；子公司看自身+子孙
export function scopeByOrg(records, user, field = 'subsidiary') {
  if (!user || user.role !== 'subsidiary') return records || [];
  const allowed = user.orgDescendantIds && user.orgDescendantIds.length
    ? new Set(user.orgDescendantIds)
    : getDescendantIds(user.orgNode || user.subsidiary);
  return (records || []).filter((r) => r[field] && allowed.has(r[field]));
}

// 按用户组织子树过滤记录（数组归属字段，如黑名单 affectedSubsidiaries / 供应商 subsidiaries）
export function scopeArrayByOrg(records, user, field = 'affectedSubsidiaries') {
  if (!user || user.role !== 'subsidiary') return records || [];
  const allowed = user.orgDescendantIds && user.orgDescendantIds.length
    ? new Set(user.orgDescendantIds)
    : getDescendantIds(user.orgNode || user.subsidiary);
  return (records || []).filter((r) => (r[field] || []).some((id) => allowed.has(id)));
}

// 判断是否应显示"提报单位"列：集团或管辖多节点的二级
export function shouldShowOrgColumn(user) {
  if (!user) return false;
  if (user.role === 'group') return true;
  return user.orgLevel === 2 && (user.orgDescendantIds || []).length > 1;
}
